// Bloco renderizado da questão atual — usado em todos os modos do aluno:
// Modo Estudo, Modo Prova (em andamento), Revisão de prova encerrada e
// Resultado da prova. Substitui as funções paralelas QuestionView
// (StudyWorkspace) e ProvaQuestionView (ProvaWorkspace) por uma única
// fonte de verdade.
//
// Eixos de variação (props):
//   feedback: "none"        → nenhum verde/vermelho (prova em andamento)
//             "answered"    → alternativa selecionada em verde se acertou,
//                             vermelha se errou; alternativa correta não
//                             selecionada destacada em verde pálido (quando
//                             showCorrectWhenWrong=true)
//   onSelect              → callback ao clicar; omitido = read-only
//   onClear               → callback do "Limpar resposta"; omitido = sem botão
//   showCorrectWhenWrong  → quando feedback="answered" e errou, marca a certa
//   hideTema/hideDificuldade → esconde badges (modo prova com config oculta)
//   showExplanation       → mostra bloco de explicação pós-resposta (Study/Revisão)
//   showStatusSummary     → mostra "Você acertou!" / "Resposta incorreta." (Study)

import { useEffect, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  Clock,
  Eye,
  EyeOff,
  FileText,
  Info,
  ListChecks,
  Loader2,
  PenLine,
  RefreshCw,
  RotateCcw,
  Sparkles,
  SpellCheck,
  X as XIcon,
  ZoomIn
} from "lucide-react";
import type { EssayResponseState, QuestaoItem, QuestaoStudy } from "./api";
import { api } from "./api";
import { aiCorrectionPrefs, strictnessLabel } from "./aiPrefs";
import { QuestionBadges } from "./questionBadges";
import {
  QuestionTable,
  isAnulada,
  renderBlock,
  renderInline,
  lightboxImg,
  correctIdx,
  isAcerto,
  ensureKaTeX,
  renderMathIn,
  aggregateCompositeScore
} from "./StudyWorkspace";
import { formatHMS } from "./ProvaWorkspace";
import { friendlyError } from "@app/ui/lib/friendly-error";

export type QuestionFeedback = "none" | "answered";

export interface QuestionCardProps {
  q: QuestaoStudy;
  selected: number | undefined;
  onSelect?: (idx: number) => void;
  onClear?: () => void;
  feedback: QuestionFeedback;
  showCorrectWhenWrong?: boolean;
  hideTema?: boolean;
  hideDificuldade?: boolean;
  showExplanation?: boolean;
  showStatusSummary?: boolean;
  /** Tempo gasto na questão (ms). Quando > 0, renderiza badge tabular no
   *  header (junto dos QuestionBadges). Modo Prova passa o tempo live. */
  tempoMs?: number;
  /** Map de respostas pra render de items (questão composta). Cada chave é
   *  o id de um item; o valor é a opção marcada (0-based). Quando ausente,
   *  questão composta não conseguirá refletir respostas individuais — passe
   *  sempre nos contextos que usam items[]. */
  respostasMap?: Record<string, number>;
  /** Callback de seleção em alternativa DE ITEM (questão composta). Recebe
   *  itemId + idx 0-based. Mantém `onSelect` separado pra retrocompat de
   *  questão simples. */
  onSelectItem?: (itemId: string, idx: number) => void;
  /** Callback pra limpar resposta de item. */
  onClearItem?: (itemId: string) => void;
  /** Bank id corrente — necessário pra essay grading (save texto, chamar IA).
   *  Quando ausente, blocos essay ficam read-only sem correção. */
  bankId?: string;
  /** Map de respostas essay por chave (item.id ou q.id). */
  essayResponses?: Record<string, EssayResponseState>;
  /** Callback após o estado essay mudar (save texto OU correção IA). Modo
   *  Estudo deve mesclar isso no `data.estudo.essayResponses` em memória. */
  onEssayChange?: (key: string, state: EssayResponseState) => void;
  /** Callback "Recomeçar questão" — limpa resposta, tempo e correção IA
   *  daquela questão (ou todos os items[] da composta). Quando ausente, o
   *  botão não aparece (Revisão / Prova têm read-only). */
  onResetQuestion?: () => void;
  /** Em prova ativa: autosave da essay NÃO chama bank-essay endpoint (que
   *  escreve em estudo bucket). Persistência via onEssayChange → PUT
   *  /bank-prova-active. Karol 2026-05-24 (buckets separadas). */
  provaMode?: boolean;
}

export function QuestionCard({
  q,
  selected,
  onSelect,
  onClear,
  feedback,
  showCorrectWhenWrong = true,
  hideTema,
  hideDificuldade,
  showExplanation = false,
  showStatusSummary = false,
  tempoMs = 0,
  respostasMap,
  onSelectItem,
  onClearItem,
  bankId,
  essayResponses,
  onEssayChange,
  onResetQuestion,
  provaMode
}: QuestionCardProps) {
  const composta = Array.isArray(q.items) && q.items.length >= 2;
  const isEssay = !composta && q.tipo === "essay";
  const anulada = isAnulada(q);
  const corrIdx = correctIdx(q);
  const answered = selected !== undefined;
  const acertou = answered && isAcerto(q, selected);

  // Nota ponderada da questão composta (só renderiza quando há essay items
  // ou MC items respondidos). Calculada aqui pra ficar logo após as badges,
  // antes da Situação — feedback contextual antes do conteúdo.
  const showCompositeAggregate =
    composta && feedback === "answered";
  const compositeAggregate = showCompositeAggregate
    ? aggregateCompositeScore(q, respostasMap ?? {}, essayResponses ?? {})
    : null;

  // "Recomeçar questão" disponível quando há algo pra apagar (resposta
  // marcada, texto digitado ou correção IA persistida em qualquer chave
  // relacionada).
  const algumaRespostaMc = composta
    ? (q.items ?? []).some((it) => respostasMap?.[it.id] !== undefined)
    : selected !== undefined;
  const algumEssay = (() => {
    if (!essayResponses) return false;
    if (composta) {
      return (q.items ?? []).some((it) => {
        const s = essayResponses[it.id];
        return s && (!!s.resposta || s.gradedAt);
      });
    }
    if (isEssay) {
      const s = essayResponses[q.id];
      return !!s && (!!s.resposta || !!s.gradedAt);
    }
    return false;
  })();
  const podeRecomecar = !!onResetQuestion && (algumaRespostaMc || algumEssay);
  const articleRef = useRef<HTMLElement>(null);
  const showFeedback = feedback === "answered" && answered;
  const readOnly = !onSelect;

  // KaTeX render em mount + sempre que a questão muda OU o estado de
  // resposta muda (explicação pode aparecer com math).
  useEffect(() => {
    void ensureKaTeX().then(() => renderMathIn(articleRef.current));
  }, [q.id, answered, anulada]);

  return (
    <article
      ref={articleRef}
      class="space-y-4 rounded-md border border-border bg-card p-6"
    >
      <div class="flex flex-wrap items-center gap-2">
        <div class="min-w-0 flex-1">
          <QuestionBadges
            q={q}
            anulada={anulada}
            ocultarTema={hideTema}
            ocultarDificuldade={hideDificuldade}
          />
        </div>
        {tempoMs > 0 && (
          <span
            class="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground"
            title="Tempo nesta questão"
          >
            <Clock className="size-3" />
            {formatHMS(tempoMs)}
          </span>
        )}
        {podeRecomecar && (
          <button
            type="button"
            onClick={() => onResetQuestion?.()}
            title="Limpa resposta, correção da IA e tempo desta questão"
            class="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcw className="size-3" />
            Recomeçar questão
          </button>
        )}
      </div>

      {/* Nota ponderada da composta — sobe pra cá pra dar o feedback ANTES
          do enunciado/situação. Threshold ≥7 explicado no próprio card. */}
      {compositeAggregate && (
        <CompositeAggregateSummary aggregate={compositeAggregate} />
      )}

      {/* Situação (caso clínico) */}
      {q.caso && (
        <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-4 py-3 text-sm">
          <div class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Situação
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.caso) }}
          />
        </div>
      )}

      {/* Tabelas */}
      {Array.isArray(q.tabelas) && q.tabelas.length > 0 && (
        <div class="space-y-3">
          {q.tabelas.map((t, i) => (
            <QuestionTable key={i} table={t} />
          ))}
        </div>
      )}

      {/* Imagens */}
      <QuestionImages q={q} />

      {/* Enunciado */}
      <div
        class="prose-question text-base leading-relaxed"
        dangerouslySetInnerHTML={{ __html: renderBlock(q.pergunta) }}
      />

      {/* === QUESTÃO COMPOSTA: cada item como sub-card === */}
      {composta && (
        <CompositeItemsBlock
          q={q}
          respostasMap={respostasMap ?? {}}
          feedback={feedback}
          showCorrectWhenWrong={showCorrectWhenWrong}
          showExplanation={showExplanation}
          onSelectItem={onSelectItem}
          onClearItem={onClearItem}
          bankId={bankId}
          essayResponses={essayResponses}
          onEssayChange={onEssayChange}
          provaMode={provaMode}
        />
      )}

      {/* === BLOCO DISSERTATIVA SIMPLES === */}
      {isEssay && (
        <EssayQuestionBody
          q={q}
          showGabarito={showExplanation}
          bankId={bankId}
          essayState={essayResponses?.[q.id]}
          onEssayChange={onEssayChange}
          provaMode={provaMode}
        />
      )}

      {/* Alternativas — só pra múltipla escolha simples */}
      {!composta && !isEssay && (
      <ul class="space-y-2">
        {(q.opcoes ?? []).map((opt, i) => {
          const letter = String.fromCharCode(65 + i);
          const isSelected = selected === i;
          const isCorrectAlt = i === corrIdx;

          let classes = "border-border bg-card/60";
          if (!readOnly && !anulada) classes += " hover:bg-accent/60";
          let badgeClasses = "border-border bg-background";
          let trailing: any = null;

          if (anulada) {
            classes = "border-border bg-card/30 cursor-not-allowed opacity-60";
          } else if (showFeedback) {
            if (isSelected && acertou) {
              // Verde forte e vibrante quando o aluno acerta — fundo
              // saturado, borda destacada e brilho/glow sutil em volta.
              classes =
                "border-emerald-500 bg-emerald-500/30 ring-2 ring-emerald-500/60 shadow-lg shadow-emerald-500/30";
              badgeClasses = "border-emerald-500 bg-emerald-500 text-white";
              trailing = <Check className="size-5 text-emerald-500" strokeWidth={3} />;
            } else if (isSelected && !acertou) {
              classes =
                "border-red-500/60 bg-red-500/15 ring-1 ring-red-500/40";
              badgeClasses = "border-red-500/60 bg-red-500 text-white";
              trailing = <XIcon className="size-4 text-red-500" />;
            } else if (!isSelected && isCorrectAlt && showCorrectWhenWrong) {
              // Errou: ainda destaca a CORRETA com verde forte (saturado),
              // só com brilho menor que o "acertou" — aluno precisa ver
              // claramente qual era a resposta certa.
              classes =
                "border-emerald-500 bg-emerald-500/20 ring-2 ring-emerald-500/50";
              badgeClasses =
                "border-emerald-500 bg-emerald-500 text-white";
              trailing = <Check className="size-5 text-emerald-500" strokeWidth={3} />;
            } else {
              classes = "border-border bg-card/30 opacity-70";
            }
          } else if (isSelected) {
            // Sem feedback: estado neutro selecionado (prova em andamento)
            classes = "border-primary bg-primary/5 ring-1 ring-primary/30";
            badgeClasses = "border-primary bg-primary text-white";
          }

          // Suporta alternativa em IMAGEM (objeto {imagem, legenda?}) OU
          // texto puro (string). [[project-alternativas-imagem]]
          const isImageOpt =
            typeof opt === "object" && opt !== null && "imagem" in (opt as Record<string, unknown>);
          return (
            <li key={i}>
              <button
                type="button"
                disabled={anulada || readOnly}
                onClick={() => onSelect?.(i)}
                aria-pressed={isSelected ? "true" : "false"}
                class={`flex w-full ${
                  isImageOpt ? "items-start" : "items-center"
                } gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors ${classes}`}
              >
                <span
                  class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${badgeClasses}`}
                >
                  {letter}
                </span>
                {isImageOpt ? (
                  <AltImageBlock
                    src={(opt as { imagem: string }).imagem}
                    legenda={(opt as { legenda?: string }).legenda}
                  />
                ) : (
                  <span
                    class="flex-1 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: renderInline(String(opt ?? ""))
                    }}
                  />
                )}
                {trailing && <span class="shrink-0">{trailing}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      )}

      {/* Status summary + Limpar (modo Estudo) — só pra questão simples;
          composta gerencia status item-a-item. */}
      {!composta && showStatusSummary && answered && !anulada && (
        <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-accent/20 px-4 py-2 text-sm">
          <span
            class={`font-medium ${
              acertou
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {acertou ? "Você acertou!" : "Resposta incorreta."}
          </span>
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              class="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <RotateCcw width="1.3em" height="1.3em" /> Limpar resposta
            </button>
          )}
        </div>
      )}

      {/* "Limpar resposta" minimal (modo Prova) — só quando não tem summary */}
      {!composta && !showStatusSummary && onClear && answered && !anulada && (
        <div class="flex items-center justify-end">
          <button
            type="button"
            onClick={onClear}
            class="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            Limpar resposta
          </button>
        </div>
      )}

      {/* Explicação (Study mode quando respondeu OU anulada; Revisão sempre).
          Em composta, explicação fica por item (dentro do CompositeItemsBlock). */}
      {!composta && showExplanation && (answered || anulada) && q.explicacao && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info width="1.3em" height="1.3em" />
            Explicação
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.explicacao) }}
          />
        </div>
      )}
    </article>
  );
}

// =============================================================================
// Questão composta — bloco de items (a/b/c sob mesmo caso)
// =============================================================================
//
// Cada item é uma sub-questão própria: pode ser MC ou Essay, tem seu próprio
// gabarito, peso e (Fase 5) correção IA. O caso, imagens, tabelas e
// fragmentos vivem na questão pai e já apareceram acima.

function CompositeItemsBlock({
  q,
  respostasMap,
  feedback,
  showCorrectWhenWrong,
  showExplanation,
  onSelectItem,
  onClearItem,
  bankId,
  essayResponses,
  onEssayChange,
  provaMode
}: {
  q: QuestaoStudy;
  respostasMap: Record<string, number>;
  feedback: QuestionFeedback;
  showCorrectWhenWrong: boolean;
  showExplanation: boolean;
  onSelectItem?: (itemId: string, idx: number) => void;
  onClearItem?: (itemId: string) => void;
  bankId?: string;
  essayResponses?: Record<string, EssayResponseState>;
  onEssayChange?: (key: string, state: EssayResponseState) => void;
  provaMode?: boolean;
}) {
  const items = q.items ?? [];
  // Nota: a CompositeAggregateSummary saiu daqui — sobe pra QuestionCard
  // pra aparecer logo após as badges (antes da Situação).
  return (
    <ol class="space-y-4">
      {items.map((it) => (
        <li key={it.id}>
          <CompositeItemCard
            it={it}
            qid={q.id}
            resposta={respostasMap[it.id]}
            feedback={feedback}
            showCorrectWhenWrong={showCorrectWhenWrong}
            showExplanation={showExplanation}
            onSelect={onSelectItem ? (idx) => onSelectItem(it.id, idx) : undefined}
            onClear={onClearItem ? () => onClearItem(it.id) : undefined}
            bankId={bankId}
            essayState={essayResponses?.[it.id]}
            onEssayChange={onEssayChange}
            provaMode={provaMode}
          />
        </li>
      ))}
    </ol>
  );
}

// Card resumo da nota ponderada de uma questão composta. Mostra a nota
// final (0–10), o threshold dinâmico (≥ aiCorrectionPrefs.threshold pra ser
// correta — configurável em Minha Conta) e o breakdown peso × nota item a
// item. Aparece sempre acima da lista de items no Modo Estudo (feedback="answered").
function CompositeAggregateSummary({
  aggregate
}: {
  aggregate: import("./StudyWorkspace").CompositeAggregate;
}) {
  const { notaFinal, totalPeso, itens, pendente } = aggregate;
  const threshold = aiCorrectionPrefs.value.threshold;
  const aprovado = !pendente && notaFinal >= threshold;
  // Tom da borda/fundo: pendente = roxo discreto, aprovado = verde, abaixo = vermelho.
  const tone = pendente
    ? "border-violet-500/30 bg-violet-500/5"
    : aprovado
      ? "border-emerald-500/40 bg-emerald-500/5"
      : "border-red-500/40 bg-red-500/5";
  const notaCor = pendente
    ? "text-violet-700 dark:text-violet-300"
    : aprovado
      ? "text-emerald-700 dark:text-emerald-300"
      : "text-red-700 dark:text-red-300";
  return (
    <section class={`space-y-3 rounded-md border p-4 ${tone}`}>
      <header class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <ListChecks
            className={`size-4 ${
              pendente
                ? "text-violet-500"
                : aprovado
                  ? "text-emerald-500"
                  : "text-red-500"
            }`}
          />
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Nota da questão (ponderada)
            </div>
            <div class="text-[11px] text-muted-foreground">
              Considera-se correta com nota ≥{" "}
              <strong>{threshold}</strong>.{" "}
              {pendente && "Responda todos os itens para ver a nota final."}
            </div>
          </div>
        </div>
        <span class={`font-bold tabular-nums ${notaCor}`}>
          <span class="text-2xl">{notaFinal.toFixed(2).replace(".", ",")}</span>
          <span class="text-xs opacity-70"> / 10</span>
        </span>
      </header>

      {/* Breakdown por item — peso, nota individual, contribuição final */}
      <div class="overflow-hidden rounded-md border border-border/60 bg-background/40">
        <table class="w-full text-[11px] leading-tight">
          <thead class="bg-accent/30 text-muted-foreground">
            <tr>
              <th class="px-2 py-1.5 text-left font-semibold uppercase tracking-wider">
                Item
              </th>
              <th class="px-2 py-1.5 text-left font-semibold uppercase tracking-wider">
                Tipo
              </th>
              <th class="px-2 py-1.5 text-right font-semibold uppercase tracking-wider">
                Peso
              </th>
              <th class="px-2 py-1.5 text-right font-semibold uppercase tracking-wider">
                Nota (0–10)
              </th>
              <th class="px-2 py-1.5 text-right font-semibold uppercase tracking-wider">
                Contribuição
              </th>
            </tr>
          </thead>
          <tbody>
            {itens.map((row) => {
              const pesoPct = totalPeso > 0 ? (row.peso / totalPeso) * 100 : 0;
              const sem = row.notaItem == null;
              return (
                <tr
                  key={row.id}
                  class="border-t border-border/40"
                >
                  <td class="px-2 py-1.5 font-bold">{row.label}</td>
                  <td class="px-2 py-1.5 text-muted-foreground">
                    {row.kind === "essay" ? "Dissertativa" : "Múltipla escolha"}
                  </td>
                  <td class="px-2 py-1.5 text-right tabular-nums">
                    {row.peso.toLocaleString("pt-BR")}{" "}
                    <span class="text-muted-foreground">
                      ({pesoPct.toFixed(0)}%)
                    </span>
                  </td>
                  <td
                    class={`px-2 py-1.5 text-right tabular-nums ${
                      sem
                        ? "text-muted-foreground"
                        : (row.notaItem ?? 0) >= threshold
                          ? "text-emerald-700 dark:text-emerald-300"
                          : "text-red-700 dark:text-red-300"
                    }`}
                  >
                    {sem ? "—" : (row.notaItem ?? 0).toFixed(2).replace(".", ",")}
                  </td>
                  <td class="px-2 py-1.5 text-right tabular-nums">
                    {sem
                      ? "—"
                      : row.ganho.toFixed(2).replace(".", ",")}{" "}
                    <span class="text-muted-foreground">/ {row.peso}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CompositeItemCard({
  it,
  qid,
  resposta,
  feedback,
  showCorrectWhenWrong,
  showExplanation,
  onSelect,
  onClear,
  bankId,
  essayState,
  onEssayChange,
  provaMode
}: {
  it: QuestaoItem;
  qid: string;
  resposta: number | undefined;
  feedback: QuestionFeedback;
  showCorrectWhenWrong: boolean;
  showExplanation: boolean;
  onSelect?: (idx: number) => void;
  onClear?: () => void;
  bankId?: string;
  essayState?: EssayResponseState;
  onEssayChange?: (key: string, state: EssayResponseState) => void;
  provaMode?: boolean;
}) {
  const isEssay = it.tipo === "essay";
  const isMcAnulada = !isEssay && it.correta === 0;
  const accent =
    isEssay
      ? "border-violet-500/30 bg-violet-500/5"
      : "border-blue-500/30 bg-blue-500/5";
  const labelBadge =
    isEssay
      ? "bg-violet-500/20 text-violet-700 dark:text-violet-300"
      : "bg-blue-500/20 text-blue-700 dark:text-blue-300";

  const corrIdx =
    typeof it.correta === "number" && it.correta >= 1 ? it.correta - 1 : -1;
  const answered = !isEssay && resposta !== undefined;
  const acertou =
    answered &&
    (isMcAnulada ||
      (typeof resposta === "number" && resposta === corrIdx));
  const showFeedback = feedback === "answered" && answered;
  const readOnly = !onSelect;

  return (
    <article class={`space-y-3 rounded-md border p-4 ${accent}`}>
      <header class="flex flex-wrap items-center gap-2">
        <span class={`grid size-7 shrink-0 place-items-center rounded-md text-xs font-bold ${labelBadge}`}>
          {it.label}
        </span>
        <span
          class={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
            isEssay
              ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
              : "bg-blue-500/15 text-blue-700 dark:text-blue-300"
          }`}
        >
          {isEssay ? "Dissertativa" : "Múltipla escolha"}
        </span>
        {isMcAnulada && (
          <span class="rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            Anulada
          </span>
        )}
        {typeof it.notaQuestao === "number" && it.notaQuestao > 0 && (
          <span class="ml-auto text-[11px] text-muted-foreground">
            Peso {it.notaQuestao}
          </span>
        )}
        {showFeedback && !isMcAnulada && (
          <span
            class={`ml-auto inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold ${
              acertou
                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : "bg-red-500/15 text-red-700 dark:text-red-300"
            }`}
          >
            {acertou ? <Check className="size-3" /> : <XIcon className="size-3" />}
            {acertou ? "Acertou" : "Errou"}
          </span>
        )}
      </header>

      <div
        class="prose-question text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: renderBlock(it.pergunta || "") }}
      />

      {isEssay ? (
        <CompositeItemEssay
          it={it}
          qid={qid}
          showGabarito={showExplanation}
          bankId={bankId}
          essayState={essayState}
          onEssayChange={onEssayChange}
          provaMode={provaMode}
        />
      ) : (
        <CompositeItemMc
          it={it}
          resposta={resposta}
          showFeedback={showFeedback}
          showCorrectWhenWrong={showCorrectWhenWrong}
          isMcAnulada={isMcAnulada}
          readOnly={readOnly}
          onSelect={onSelect}
          onClear={onClear}
        />
      )}

      {/* Explicação MC item (mesma lógica de questão simples mas dentro do
          sub-card) — só aparece quando aluno já respondeu E a explicação
          existe. */}
      {showExplanation && !isEssay && (answered || isMcAnulada) && it.explicacao && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-3 py-2 text-sm">
          <div class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info className="size-3" />
            Explicação do item
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(it.explicacao) }}
          />
        </div>
      )}
    </article>
  );
}

function CompositeItemMc({
  it,
  resposta,
  showFeedback,
  showCorrectWhenWrong,
  isMcAnulada,
  readOnly,
  onSelect,
  onClear
}: {
  it: QuestaoItem;
  resposta: number | undefined;
  showFeedback: boolean;
  showCorrectWhenWrong: boolean;
  isMcAnulada: boolean;
  readOnly: boolean;
  onSelect?: (idx: number) => void;
  onClear?: () => void;
}) {
  const corrIdx =
    typeof it.correta === "number" && it.correta >= 1 ? it.correta - 1 : -1;
  const acertou =
    resposta !== undefined &&
    (isMcAnulada || resposta === corrIdx);
  const opcoes = it.opcoes ?? [];

  return (
    <>
      <ul class="space-y-2">
        {opcoes.map((opt, i) => {
          const letter = String.fromCharCode(65 + i);
          const isSelected = resposta === i;
          const isCorrectAlt = i === corrIdx;

          let classes = "border-border bg-card/60";
          if (!readOnly && !isMcAnulada) classes += " hover:bg-accent/60";
          let badgeClasses = "border-border bg-background";
          let trailing: any = null;

          if (isMcAnulada) {
            classes = "border-border bg-card/30 cursor-not-allowed opacity-60";
          } else if (showFeedback) {
            if (isSelected && acertou) {
              classes =
                "border-emerald-500 bg-emerald-500/30 ring-2 ring-emerald-500/60 shadow-lg shadow-emerald-500/30";
              badgeClasses = "border-emerald-500 bg-emerald-500 text-white";
              trailing = <Check className="size-5 text-emerald-500" strokeWidth={3} />;
            } else if (isSelected && !acertou) {
              classes = "border-red-500/60 bg-red-500/15 ring-1 ring-red-500/40";
              badgeClasses = "border-red-500/60 bg-red-500 text-white";
              trailing = <XIcon className="size-4 text-red-500" />;
            } else if (!isSelected && isCorrectAlt && showCorrectWhenWrong) {
              classes =
                "border-emerald-500 bg-emerald-500/20 ring-2 ring-emerald-500/50";
              badgeClasses = "border-emerald-500 bg-emerald-500 text-white";
              trailing = <Check className="size-5 text-emerald-500" strokeWidth={3} />;
            } else {
              classes = "border-border bg-card/30 opacity-70";
            }
          } else if (isSelected) {
            classes = "border-primary bg-primary/5 ring-1 ring-primary/30";
            badgeClasses = "border-primary bg-primary text-white";
          }

          const isImageOpt =
            typeof opt === "object" && opt !== null && "imagem" in (opt as Record<string, unknown>);
          return (
            <li key={i}>
              <button
                type="button"
                disabled={isMcAnulada || readOnly}
                onClick={() => onSelect?.(i)}
                aria-pressed={isSelected ? "true" : "false"}
                class={`flex w-full ${
                  isImageOpt ? "items-start" : "items-center"
                } gap-3 rounded-md border px-4 py-2.5 text-left text-sm transition-colors ${classes}`}
              >
                <span class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${badgeClasses}`}>
                  {letter}
                </span>
                {isImageOpt ? (
                  <AltImageBlock
                    src={(opt as { imagem: string }).imagem}
                    legenda={(opt as { legenda?: string }).legenda}
                  />
                ) : (
                  <span
                    class="flex-1 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: renderInline(String(opt ?? ""))
                    }}
                  />
                )}
                {trailing && <span class="shrink-0">{trailing}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {/* Limpar resposta do item */}
      {!isMcAnulada && onClear && resposta !== undefined && (
        <div class="flex items-center justify-end">
          <button
            type="button"
            onClick={onClear}
            class="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            <RotateCcw width="1em" height="1em" /> Limpar resposta deste item
          </button>
        </div>
      )}
    </>
  );
}

function CompositeItemEssay({
  it,
  qid,
  showGabarito,
  bankId,
  essayState,
  onEssayChange,
  provaMode
}: {
  it: QuestaoItem;
  qid: string;
  showGabarito: boolean;
  bankId?: string;
  essayState?: EssayResponseState;
  onEssayChange?: (key: string, state: EssayResponseState) => void;
  provaMode?: boolean;
}) {
  // Mesmo padrão do EssayQuestionBody: gabarito do item só aparece
  // depois do aluno clicar em "Ver gabarito".
  const [revealGabarito, setRevealGabarito] = useState(false);
  useEffect(() => {
    setRevealGabarito(false);
  }, [it.id]);
  return (
    <div class="space-y-3">
      <EssayAnswerBox
        bankId={bankId}
        qid={qid}
        itemId={it.id}
        essayState={essayState}
        onEssayChange={onEssayChange}
        allowGrading={showGabarito}
        provaMode={provaMode}
        placeholder="Escreva sua resposta para este item. Mín. 10 chars pra liberar correção."
      />
      {showGabarito && it.gabarito_modelo && (
        <div class="space-y-2">
          <button
            type="button"
            onClick={() => setRevealGabarito((v) => !v)}
            class="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-[11px] font-bold text-primary transition-colors hover:bg-primary/10"
          >
            {revealGabarito ? (
              <EyeOff className="size-3" />
            ) : (
              <Eye className="size-3" />
            )}
            {revealGabarito ? "Ocultar gabarito" : "Ver gabarito"}
          </button>
          {revealGabarito && (
            <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-3 py-2 text-sm">
              <div class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Info className="size-3" />
                Gabarito-modelo
              </div>
              <div
                class="prose-question leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderBlock(it.gabarito_modelo) }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Bloco de Questão Dissertativa (essay)
// =============================================================================
//
// Renderiza fragmentos de apoio, direcionadores, info da banca, espaço pra
// resposta do aluno (placeholder por enquanto — em breve textarea conectada
// + IA de correção) e, quando `showGabarito` é true (Modo Estudo / Revisão),
// o gabarito-modelo e os critérios de correção.

function EssayQuestionBody({
  q,
  showGabarito,
  bankId,
  essayState,
  onEssayChange,
  provaMode
}: {
  q: QuestaoStudy;
  showGabarito: boolean;
  bankId?: string;
  essayState?: EssayResponseState;
  onEssayChange?: (key: string, state: EssayResponseState) => void;
  provaMode?: boolean;
}) {
  const fragmentos = Array.isArray(q.fragmentos) ? q.fragmentos : [];
  const criterios = Array.isArray(q.criterios) ? q.criterios : [];
  const competencias = Array.isArray(q.competencias) ? q.competencias : [];
  // Gabarito/critérios não aparecem direto — aluno clica em "Ver gabarito"
  // pra revelar, evitando ver a resposta antes de tentar escrever.
  const [revealGabarito, setRevealGabarito] = useState(false);
  // Quando muda de questão, esconde de novo.
  useEffect(() => {
    setRevealGabarito(false);
  }, [q.id]);
  const temGabarito =
    !!q.gabarito_modelo || criterios.length > 0 || competencias.length > 0;
  return (
    <div class="space-y-4">
      {/* Linha de metadados da banca */}
      {(q.banca || q.genero || q.linhas_min || q.linhas_max || q.nota_maxima) && (
        <div class="flex flex-wrap items-center gap-2 text-[11px]">
          {q.banca && (
            <span class="rounded-md bg-violet-500/15 px-2 py-0.5 font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              {q.banca}
            </span>
          )}
          {q.genero && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              {q.genero}
            </span>
          )}
          {(q.linhas_min || q.linhas_max) && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              {q.linhas_min && q.linhas_max
                ? `${q.linhas_min}–${q.linhas_max} linhas`
                : q.linhas_max
                  ? `até ${q.linhas_max} linhas`
                  : `mín. ${q.linhas_min} linhas`}
            </span>
          )}
          {typeof q.nota_maxima === "number" && q.nota_maxima > 0 && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              Nota máx. {q.nota_maxima}
            </span>
          )}
        </div>
      )}

      {/* Fragmentos / textos motivadores */}
      {fragmentos.length > 0 && (
        <div class="space-y-2">
          <div class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <FileText className="size-3" />
            Textos motivadores ({fragmentos.length})
          </div>
          <div class="space-y-2">
            {fragmentos.map((f, i) => (
              <div
                key={i}
                class="rounded-md border-l-4 border-violet-500/40 bg-violet-500/5 px-4 py-3"
              >
                {f.label && (
                  <div class="mb-1 text-[11px] font-bold text-violet-700 dark:text-violet-300">
                    {f.label}
                  </div>
                )}
                <div
                  class="prose-question text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderBlock(f.textContent ?? "") }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Direcionadores da proposta */}
      {q.direcionadores && (
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <ListChecks className="size-3" />
            Instruções da proposta
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.direcionadores) }}
          />
        </div>
      )}

      {/* Espaço de resposta + correção IA. allowGrading=showGabarito gate-ia:
          em modo prova pré-finalize, showGabarito=false → esconde botão
          "Corrigir com IA" e qualquer nota visível. Aluno só tenta corrigir
          depois que entrega. */}
      <EssayAnswerBox
        bankId={bankId}
        qid={q.id}
        itemId={null}
        essayState={essayState}
        onEssayChange={onEssayChange}
        allowGrading={showGabarito}
        provaMode={provaMode}
        placeholder="Escreva sua resposta dissertativa aqui. Pelo menos 10 caracteres pra liberar a correção da IA."
      />

      {/* Gabarito-modelo + critérios — gated por toggle no Modo Estudo pra
          o aluno não ver a resposta antes de tentar. */}
      {showGabarito && temGabarito && (
        <div class="space-y-2">
          <button
            type="button"
            onClick={() => setRevealGabarito((v) => !v)}
            class="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary transition-colors hover:bg-primary/10"
            title={
              revealGabarito
                ? "Esconder o gabarito da questão"
                : "Mostrar o gabarito da questão"
            }
          >
            {revealGabarito ? (
              <EyeOff className="size-3.5" />
            ) : (
              <Eye className="size-3.5" />
            )}
            {revealGabarito ? "Ocultar gabarito" : "Ver gabarito"}
          </button>
          {revealGabarito && q.gabarito_modelo && (
            <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-4 py-3 text-sm">
              <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Info className="size-3" />
                Gabarito-modelo
              </div>
              <div
                class="prose-question leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderBlock(q.gabarito_modelo) }}
              />
            </div>
          )}
          {revealGabarito && (criterios.length > 0 || competencias.length > 0) && (
            <div class="rounded-md border border-border bg-card/40 px-4 py-3 text-sm">
              <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <ListChecks className="size-3" />
                Critérios de correção
              </div>
              {criterios.length > 0 && (
                <ul class="ml-4 list-disc space-y-1 text-[13px] leading-snug">
                  {criterios.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              )}
              {competencias.length > 0 && (
                <>
                  <div class="mt-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Competências adicionais
                  </div>
                  <ul class="ml-4 list-disc space-y-1 text-[13px] leading-snug">
                    {competencias.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EssayAnswerBox — caixa de resposta dissertativa com autosave + IA grading
// =============================================================================
//
// Mesma caixa serve essay simples (itemId=null, chave = q.id) e item de
// questão composta (itemId preenchido, chave = item.id). Persistência:
// `PATCH /api/student/bank-essay?bankId&qid&itemId` salva texto;
// `POST  ...&action=grade` chama IA, devolve nota + feedback.
//
// Threshold de aprovação ≥7 vira indicador verde, <7 vermelho — refletido
// no card e no status agregado da questão (questaoStatus).

function EssayAnswerBox({
  bankId,
  qid,
  itemId,
  essayState,
  onEssayChange,
  placeholder,
  allowGrading = true,
  provaMode = false
}: {
  bankId?: string;
  qid: string;
  itemId: string | null;
  essayState?: EssayResponseState;
  onEssayChange?: (key: string, state: EssayResponseState) => void;
  placeholder?: string;
  /** Quando false, esconde botão "Corrigir com IA" e bloco de nota. Usado
   *  pra travar correção durante modo prova até o aluno finalizar — sem
   *  isso o aluno consegue ver feedback da IA durante a simulação. */
  allowGrading?: boolean;
  /** Em prova ativa: autosave NÃO chama bank-essay endpoint (que escreve
   *  na bucket estudo). Persistência via onEssayChange → prova-active PUT.
   *  Pedido Karol 2026-05-24 pra separar buckets Study vs Prova. */
  provaMode?: boolean;
}) {
  const key = itemId ?? qid;
  const [draft, setDraft] = useState<string>(essayState?.resposta ?? "");
  const [savingState, setSavingState] = useState<"idle" | "saving" | "saved" | "dirty">(
    essayState?.resposta ? "saved" : "idle"
  );
  const [grading, setGrading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [gradeErr, setGradeErr] = useState<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>(essayState?.resposta ?? "");
  // Espelho do draft pra usar dentro de listeners de beforeunload sem
  // re-registrar a cada keystroke (refs não disparam re-render).
  const draftRef = useRef<string>(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  // Flush em beforeunload/pagehide E NO UNMOUNT: se há save pendente quando
  // o aluno fecha aba, recarrega OU sai do workspace (clica "Voltar pra bancos"
  // dentro de 1.2s), dispara o PATCH com keepalive pra garantir persistência.
  // Karol 2026-05-29: digitar "Teste" e clicar Voltar dentro do debounce
  // perdia a resposta — beforeunload só dispara em navegação real do browser,
  // não em desmonte do React. Solução: o cleanup do useEffect agora também
  // flusha (sem precisar que o browser saia da página).
  useEffect(() => {
    function flushPending() {
      if (!saveTimerRef.current || !bankId) return;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      const text = draftRef.current;
      if (text === lastSavedRef.current) return;
      const qs = new URLSearchParams({ bankId, qid });
      if (itemId) qs.set("itemId", itemId);
      try {
        void fetch(`/api/student/bank-essay?${qs.toString()}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ resposta: text }),
          keepalive: true,
          credentials: "include"
        });
        // Marca como salvo no ref pra evitar PATCH duplicado se outro
        // listener disparar logo em seguida (pagehide depois de unmount).
        lastSavedRef.current = text;
      } catch {
        /* navigator pode bloquear se aba já está fechando — vida que segue */
      }
    }
    window.addEventListener("beforeunload", flushPending);
    window.addEventListener("pagehide", flushPending);
    return () => {
      window.removeEventListener("beforeunload", flushPending);
      window.removeEventListener("pagehide", flushPending);
      // Cleanup do effect roda no UNMOUNT do componente — também flusha.
      // Cobre o caso de o aluno sair do workspace via botão "Voltar" antes
      // do debounce de 1.2s terminar (não é um page unload).
      flushPending();
    };
  }, [bankId, qid, itemId]);

  // Quando a key muda (aluno troca de questão/item), reseta o draft pra
  // o que veio do servidor — sem isso ficaria refletindo o texto da
  // questão anterior.
  useEffect(() => {
    setDraft(essayState?.resposta ?? "");
    lastSavedRef.current = essayState?.resposta ?? "";
    setSavingState(essayState?.resposta ? "saved" : "idle");
    setGradeErr(null);
  }, [key]);

  // Reset externo (botão "Recomeçar questão" do StudyWorkspace): o parent
  // zerou essayState mas a key continua a mesma. Sem este sync, o textarea
  // continuaria mostrando o texto antigo porque `draft` é estado local.
  //
  // Detecção: essayState ausente OU sem resposta+sem correção, E ainda
  // temos draft pra apagar (lastSaved não vazio). Cobre tanto reset que
  // remove a key (essayState=undefined) quanto reset que limpa os campos
  // (essayState={resposta:"", nota:null, ...}).
  useEffect(() => {
    const externalEmpty =
      !essayState || (!essayState.resposta && !essayState.gradedAt);
    if (externalEmpty && lastSavedRef.current !== "") {
      // Cancela autosave pendente — não faz sentido salvar o draft antigo
      // depois que o parent já mandou limpar.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setDraft("");
      lastSavedRef.current = "";
      setSavingState("idle");
      setGradeErr(null);
    }
  }, [essayState?.resposta, essayState?.gradedAt]);

  // Debounced autosave (1.2s) — só se houve mudança e tem bankId.
  function scheduleSave(next: string) {
    if (!bankId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSavingState("dirty");
    saveTimerRef.current = setTimeout(() => void doSave(next), 1200);
  }
  // Retry com backoff em erros transientes (rede/5xx/429). Sem isso, um
  // hiccup de conexão deixa o draft em "dirty" indefinidamente e o aluno
  // perde a última versão se fechar a aba. Decisão 2026-05-23.
  const ESSAY_RETRY_DELAYS_MS = [500, 1500, 3000, 6000];
  function isTransientEssayErr(e: unknown): boolean {
    if (!e || typeof e !== "object") return false;
    const status = (e as { status?: unknown }).status;
    if (typeof status === "number") {
      if (status === 408 || status === 429) return true;
      if (status >= 500 && status <= 599) return true;
      return false;
    }
    const msg = String((e as { message?: unknown }).message ?? "").toLowerCase();
    return (
      msg.includes("network") ||
      msg.includes("failed to fetch") ||
      msg.includes("timeout") ||
      msg.includes("offline")
    );
  }
  async function doSave(text: string, attempt: number = 0) {
    if (!bankId) return;
    if (text === lastSavedRef.current) {
      setSavingState("saved");
      return;
    }
    // Em prova ativa: pula API call (bank-essay escreve no bucket Study).
    // ProvaWorkspace.onEssayChange já atualizou prova.essayResponses +
    // disparou scheduleProvaSave — esse caminho persiste via PUT
    // /bank-prova-active na bucket prova-scoped.
    if (provaMode) {
      lastSavedRef.current = text;
      setSavingState("saved");
      return;
    }
    setSavingState("saving");
    try {
      await api.saveEssayResponse(bankId, qid, itemId, text);
      lastSavedRef.current = text;
      setSavingState("saved");
      // Reflete no estado em memória do workspace (preserva nota anterior).
      if (onEssayChange) {
        onEssayChange(key, {
          ...(essayState ?? {
            nota: null,
            feedback: null,
            status: null,
            gradedAt: null,
            provider: null,
            model: null
          }),
          resposta: text
        });
      }
    } catch (e) {
      if (isTransientEssayErr(e) && attempt < ESSAY_RETRY_DELAYS_MS.length) {
        const delay = ESSAY_RETRY_DELAYS_MS[attempt];
        console.warn(
          `[essay] save retry ${attempt + 1}/${ESSAY_RETRY_DELAYS_MS.length} em ${delay}ms:`,
          e instanceof Error ? e.message : e
        );
        setSavingState("dirty");
        await new Promise((r) => setTimeout(r, delay));
        return doSave(text, attempt + 1);
      }
      setSavingState("dirty");
      console.warn("[essay] save falhou após retries:", e);
    }
  }
  function onInput(e: Event) {
    const v = (e.target as HTMLTextAreaElement).value;
    setDraft(v);
    scheduleSave(v);
    // Optimistic: marca a questão como "respondida" no estado em memória do
    // workspace IMEDIATAMENTE, sem esperar o autosave de 1.2s. Sem isso, o
    // aluno digita mas a sidebar continua mostrando "pendente" até o save
    // completar (a Karol reportou em 2026-05-23 que parecia que o sistema
    // não estava registrando).
    if (onEssayChange) {
      onEssayChange(key, {
        ...(essayState ?? {
          nota: null,
          feedback: null,
          status: null,
          gradedAt: null,
          provider: null,
          model: null
        }),
        resposta: v
      });
    }
  }

  async function grade() {
    if (!bankId || grading) return;
    // Captura o bankId narrowed (TS não preserva narrow em closure interna).
    const bid: string = bankId;
    setGrading(true);
    setGradeErr(null);
    // Flush pending save antes de avaliar — IA precisa ler o texto certo.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      await doSave(draft);
    }
    // 1 retry automático em erros transientes (5xx / rede / timeout) — sem
    // loop infinito. Se a primeira tentativa der erro classificável como
    // "tente de novo", aguarda 800ms e tenta uma segunda vez.
    async function once(): Promise<{ ok: true; grade: any } | { ok: false; err: any }> {
      try {
        const r = await api.gradeEssayResponse(bid, qid, itemId ?? null, draft);
        return { ok: true, grade: r.grade };
      } catch (err) {
        return { ok: false, err };
      }
    }
    function isTransient(err: any): boolean {
      const status = typeof err?.status === "number" ? err.status : 0;
      const code = String(err?.code ?? "").toLowerCase();
      // 502/503/504 + provider_error + parse_error (IA devolveu lixo, regerar
      // costuma resolver) + falhas de rede.
      if (status >= 502 && status <= 504) return true;
      if (code === "provider_error" || code === "parse_error" || code.startsWith("http_5")) return true;
      if (/network|failed to fetch|timeout/i.test(String(err?.message ?? ""))) return true;
      return false;
    }
    try {
      let result = await once();
      if (!result.ok && isTransient(result.err)) {
        console.warn("[essay] tentativa 1 falhou (transiente), retentando...", result.err);
        await new Promise((r) => setTimeout(r, 800));
        result = await once();
      }
      if (result.ok) {
        if (onEssayChange) onEssayChange(key, result.grade);
      } else {
        console.error("[essay] correção IA falhou após retry:", result.err);
        setGradeErr(
          friendlyError(result.err, {
            overrides: {
              provider_error:
                "A IA não conseguiu avaliar agora. Aguarde alguns segundos e tente de novo.",
              parse_error:
                "A IA devolveu uma resposta que não consegui interpretar. Tente de novo.",
              empty_response: "Escreva a resposta antes de pedir correção.",
              no_provider: "Nenhum provedor de IA configurado. Avise o administrador."
            },
            fallback:
              "Não foi possível avaliar agora. Verifique sua conexão e tente em alguns segundos."
          })
        );
      }
    } finally {
      setGrading(false);
    }
  }

  // Limpa SÓ a caixa atual (key = itemId em composta, qid em essay simples).
  // Apaga texto e correção IA daquela chave no servidor — outros itens da
  // mesma questão composta e outras questões ficam intactos.
  async function clearResposta() {
    if (!bankId || clearing) return;
    // Confirmação inline rápida — sem modal pra manter fluxo de estudo.
    if (
      essayState?.gradedAt &&
      !window.confirm(
        "Limpar essa resposta também apaga a correção da IA. Quer continuar?"
      )
    ) {
      return;
    }
    setClearing(true);
    setGradeErr(null);
    // Cancela autosave pendente — vamos sobrescrever com vazio.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    try {
      await api.clearEssayResponse(bankId, qid, itemId);
      setDraft("");
      lastSavedRef.current = "";
      setSavingState("idle");
      if (onEssayChange) {
        onEssayChange(key, {
          resposta: "",
          nota: null,
          feedback: null,
          status: null,
          gradedAt: null,
          provider: null,
          model: null
        });
      }
    } catch (e: any) {
      console.error("[essay] limpar falhou:", e);
      setGradeErr(
        friendlyError(e, {
          fallback: "Não foi possível limpar a resposta agora. Tente de novo."
        })
      );
    } finally {
      setClearing(false);
    }
  }

  const charCount = draft.length;
  // Mínimo de 1 caractere: algumas questões aceitam "Sim", uma letra, número
  // único. Limitar em 10 estava bloqueando respostas curtas válidas. [[2026-05-25]]
  const tooShort = draft.trim().length < 1;
  const hasGrade = essayState?.nota != null;
  const isApproved =
    hasGrade && essayState!.nota! >= aiCorrectionPrefs.value.threshold;

  return (
    <div class="space-y-3">
      <div
        class={`rounded-md border bg-card/40 ${
          hasGrade
            ? isApproved
              ? "border-emerald-500/50"
              : "border-red-500/50"
            : "border-violet-500/30"
        }`}
      >
        <header class="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 text-[11px]">
          <span class="inline-flex items-center gap-1 font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
            <PenLine className="size-3" /> Sua resposta
          </span>
          <span class="text-muted-foreground">{charCount} chars</span>
          <span
            class="text-[10px] italic text-muted-foreground"
            title={`Nota mínima pra acerto configurada em Minha Conta › Preferências da IA. Atual: ${aiCorrectionPrefs.value.threshold}.`}
          >
            · correta com nota ≥ {aiCorrectionPrefs.value.threshold}
          </span>
          <span class="ml-auto inline-flex items-center gap-1 text-muted-foreground">
            {savingState === "saving" && (
              <>
                <Loader2 className="size-3 animate-spin" /> salvando…
              </>
            )}
            {savingState === "saved" && (
              <>
                <Check className="size-3 text-emerald-500" /> salvo
              </>
            )}
            {savingState === "dirty" && (
              <>
                <span class="size-1.5 rounded-full bg-amber-500" /> não salvo
              </>
            )}
          </span>
        </header>
        <textarea
          value={draft}
          onInput={onInput}
          onKeyDown={(e) => e.stopPropagation()}
          disabled={!bankId}
          placeholder={placeholder ?? "Escreva sua resposta…"}
          rows={Math.max(6, Math.min(20, Math.ceil((draft.length || 1) / 60)))}
          class="w-full resize-y bg-transparent px-3 py-2 text-sm leading-relaxed outline-none focus:bg-accent/20"
        />
        <footer class="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
          {allowGrading && (
            <button
              type="button"
              onClick={() => void grade()}
              disabled={!bankId || tooShort || grading || clearing}
              class="inline-flex h-9 items-center gap-2 rounded-md bg-violet-600 px-3 text-xs font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {grading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : essayState?.gradedAt ? (
                <RefreshCw className="size-3.5" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {grading
                ? "Corrigindo…"
                : essayState?.gradedAt
                  ? "Regerar correção"
                  : "Corrigir com IA"}
            </button>
          )}
          {/* Limpar resposta — só aparece quando há algo pra limpar (texto
              digitado OU correção persistida). Apaga só ESTE quadro; outros
              items da mesma questão composta ficam intactos. */}
          {(draft.length > 0 || essayState?.gradedAt) && (
            <button
              type="button"
              onClick={() => void clearResposta()}
              disabled={!bankId || clearing || grading}
              title="Limpar texto e correção desta resposta"
              class="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {clearing ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <RotateCcw className="size-3.5" />
              )}
              {clearing ? "Limpando…" : "Limpar resposta"}
            </button>
          )}
          {allowGrading && essayState?.gradedAt && (
            <span class="text-[10px] text-muted-foreground">
              última correção {new Date(essayState.gradedAt).toLocaleString("pt-BR")}
            </span>
          )}
          {allowGrading && tooShort && (
            <span class="ml-auto text-[10px] italic text-muted-foreground">
              escreva algo pra corrigir
            </span>
          )}
          {!allowGrading && (
            <span class="ml-auto text-[10px] italic text-muted-foreground">
              A correção pela IA libera depois que você finalizar a prova.
            </span>
          )}
        </footer>
      </div>

      {allowGrading && gradeErr && (
        <div class="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
          {gradeErr}
        </div>
      )}

      {allowGrading && hasGrade && (
        <EssayGradeView essayState={essayState!} />
      )}
    </div>
  );
}

function EssayGradeView({ essayState }: { essayState: EssayResponseState }) {
  const nota = essayState.nota ?? 0;
  // Threshold visual vem das prefs do aluno (5..8). Em Prova Agendada o
  // aluno ainda pode ter pref diferente — a nota crua é a mesma, só a
  // *interpretação* visual muda. [[ai-correction-prefs]]
  const threshold = aiCorrectionPrefs.value.threshold;
  const aprovado = nota >= threshold;
  const notaCor = aprovado
    ? "text-emerald-700 dark:text-emerald-300"
    : "text-red-700 dark:text-red-300";
  const bgBlock = aprovado
    ? "border-emerald-500/40 bg-emerald-500/5"
    : "border-red-500/40 bg-red-500/5";
  return (
    <div class={`space-y-2 rounded-md border-l-4 px-4 py-3 ${bgBlock}`}>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <Sparkles
            className={`size-4 ${aprovado ? "text-emerald-500" : "text-red-500"}`}
          />
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Correção da IA
          </span>
          {essayState.strictnessUsed && (
            <span
              class="rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-300"
              title="Modo de correção usado nesta avaliação. Configurável em Minha Conta › Preferências da IA."
            >
              Modo: {strictnessLabel(essayState.strictnessUsed)}
            </span>
          )}
        </div>
        <div class="flex items-center gap-2">
          {essayState.status && (
            <span
              class={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                aprovado
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  : "bg-red-500/15 text-red-700 dark:text-red-300"
              }`}
            >
              {essayState.status}
            </span>
          )}
          <span class={`font-bold tabular-nums ${notaCor}`}>
            <span class="text-2xl">{nota.toFixed(2).replace(".", ",")}</span>
            <span class="text-xs opacity-70"> / 10</span>
          </span>
        </div>
      </div>
      {essayState.scoreSemDesconto != null && essayState.descontoNormaCulta && (
        <p class="text-[11px] text-muted-foreground">
          Nota antes do desconto de norma culta:{" "}
          <strong class="tabular-nums">
            {essayState.scoreSemDesconto.toFixed(2).replace(".", ",")}
          </strong>{" "}
          · Desconto:{" "}
          <strong class="tabular-nums text-amber-600 dark:text-amber-400">
            −{essayState.descontoNormaCulta.pontos.toFixed(2).replace(".", ",")}
          </strong>
        </p>
      )}
      {essayState.feedback && (
        <div
          class="prose-question text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderBlock(essayState.feedback) }}
        />
      )}
      {essayState.descontoNormaCulta &&
        essayState.descontoNormaCulta.erros.length > 0 && (
          <div class="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              <SpellCheck className="size-3" />
              Erros de norma culta apontados
            </div>
            <ul class="space-y-1.5 text-xs">
              {essayState.descontoNormaCulta.erros.map((err, i) => (
                <li key={i} class="leading-snug">
                  <div class="font-medium text-amber-800 dark:text-amber-200">
                    "{err.trecho}"
                  </div>
                  <div class="text-muted-foreground">
                    {err.problema} →{" "}
                    <span class="font-medium text-foreground">{err.correto}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
}

// =============================================================================
// Alternativa-imagem (cada item de `opcoes` pode ser {imagem, legenda?})
// =============================================================================
// Renderiza a imagem dentro do card da alternativa.
//
// UX: clicar na imagem agora SELECIONA a alternativa (igual o resto da linha).
// Pra ampliar (lightbox), tem um botãozinho de lupa sobreposto no canto.
// Antes era invertido — clicar na imagem só abria lightbox e não selecionava,
// o que confundia o aluno quando a imagem ocupava a maior parte do row.
// [[bug-2026-05-28: área clicável da alternativa pequena]]

function AltImageBlock({ src, legenda }: { src: string; legenda?: string }) {
  function openLightbox(e: MouseEvent) {
    e.stopPropagation();
    lightboxImg.value = { url: src, legenda };
  }
  return (
    <span class="flex min-w-0 flex-1 flex-col gap-1">
      <span class="relative inline-block max-w-full self-start">
        <img
          src={src}
          alt={legenda ?? "Alternativa"}
          loading="lazy"
          // SEM stopPropagation — clique propaga pro botão da alternativa e
          // dispara onSelect. Quem quer ampliar usa o botão de lupa.
          class="block max-h-56 w-auto rounded-md border border-border bg-background object-contain"
        />
        <button
          type="button"
          onClick={openLightbox}
          aria-label="Ampliar imagem"
          title="Ampliar imagem"
          class="absolute right-1 top-1 inline-flex items-center justify-center rounded-md bg-black/55 p-1 text-white opacity-90 hover:opacity-100 hover:bg-black/70"
        >
          <ZoomIn className="size-3.5" />
        </button>
      </span>
      {legenda && (
        <span
          class="text-[11px] text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: renderInline(legenda) }}
        />
      )}
    </span>
  );
}

// =============================================================================
// Imagens com lightbox
// =============================================================================

function QuestionImages({ q }: { q: QuestaoStudy }) {
  const imgs: Array<{ url: string; legenda?: string }> = [];
  if (typeof q.imagem_url === "string" && q.imagem_url) {
    imgs.push({ url: q.imagem_url });
  }
  if (Array.isArray(q.imagens)) {
    for (const it of q.imagens) {
      if (typeof it === "string") imgs.push({ url: it });
      else if (it && typeof it === "object" && typeof it.url === "string") {
        imgs.push({ url: it.url, legenda: it.legenda });
      }
    }
  }
  if (imgs.length === 0) return null;
  const single = imgs.length === 1;
  return (
    <div
      class={`mx-auto grid gap-3 ${
        single ? "max-w-md" : "max-w-2xl sm:grid-cols-2"
      }`}
    >
      {imgs.map((im, i) => (
        <figure
          key={i}
          class="overflow-hidden rounded-md border border-border bg-accent/20"
        >
          <button
            type="button"
            onClick={() => (lightboxImg.value = im)}
            class="block w-full text-left transition-opacity hover:opacity-90"
            title="Clique pra ampliar"
          >
            <img
              src={im.url}
              alt={im.legenda ?? `Imagem ${i + 1}`}
              class="mx-auto block w-full cursor-zoom-in"
              loading="lazy"
            />
          </button>
          {im.legenda && (
            <figcaption
              class="border-t border-border bg-card/60 px-3 py-2 text-center text-xs text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: renderInline(im.legenda) }}
            />
          )}
        </figure>
      ))}
    </div>
  );
}
